import React, { useEffect, useState } from 'react';
import { literalWith } from '../../learner/meaning';
import { wordsOf, normalWord, wordForWordLine, hasGlosses } from '../../utils/wordForWord';
import './SubtitlePanel.css';

/**
 * The subtitle's word-for-word line as students see it (Requirement 61):
 * each French word over what it says literally, from its annotation's
 * yasna sense in the role chosen for it.
 *
 * Its gaps are where the teacher fills it in (Requirement 63):
 *   - a word nobody annotated: "+", which annotates it (onAnnotateWord)
 *   - an annotated word whose sense has no literal gloss: "+ gloss ↗",
 *     which opens that sense in yasna with a "word for word" card to fill
 *
 * Props:
 *   subtitle, annotations (the subtitle's words), yasna, language
 *   onAnnotateWord(word) — optional; without it, unannotated words show "·"
 *   always — show the line even before any word has a gloss (the Now
 *            panel); otherwise only once one has
 */
function WordForWordPreview({ subtitle, annotations = [], yasna, language = 'fr', onAnnotateWord, always = false }) {
  const [entries, setEntries] = useState({});
  const ids = [...new Set(annotations.map((a) => a.senseId).filter(Boolean))].sort().join(',');

  useEffect(() => {
    if (!yasna || !ids) return undefined;
    let cancelled = false;
    ids.split(',').forEach((id) => {
      Promise.resolve(yasna.getById(id)).then(
        (e) => { if (!cancelled && e) setEntries((prev) => (prev[id] === e ? prev : { ...prev, [id]: e })); },
        () => {}
      );
    });
    return () => { cancelled = true; };
  }, [yasna, ids]);

  const words = wordsOf(subtitle && subtitle.originalText);
  if (words.length === 0) return null;
  const literals = {};
  annotations.forEach((a) => {
    const parts = entries[a.senseId] && literalWith(yasna, entries[a.senseId], a.role);
    if (parts) literals[a.id] = parts;
  });
  const line = wordForWordLine(words, annotations, literals);
  if (!always && !hasGlosses(line)) return null;
  const byId = Object.fromEntries(annotations.map((a) => [a.id, a]));

  // What goes under a word that has no gloss yet.
  const gap = (p) => {
    const ann = p.annotationId && byId[p.annotationId];
    if (ann) {
      // Annotated, but its sense says nothing literal: fill it in yasna.
      const entry = entries[ann.senseId];
      if (!entry || !yasna || typeof yasna.teacherUrl !== 'function') {
        return <span className="wfw-pair-gloss wfw-pair-gloss--none">·</span>;
      }
      return (
        <a
          className="wfw-pair-gloss wfw-gap-link"
          href={yasna.teacherUrl(entry.word, entry.language || language, { sense: ann.senseId, card: 'word-for-word' })}
          target="_blank"
          rel="noreferrer"
          aria-label={`Add what ${entry.word} says word for word, in Yasna`}
          title="This word's dictionary entry has no word-for-word gloss yet: add it in Yasna"
        >
          + gloss ↗
        </a>
      );
    }
    if (!onAnnotateWord) return <span className="wfw-pair-gloss wfw-pair-gloss--none">·</span>;
    const bare = normalWord(p.word);
    return (
      <button
        type="button"
        className="wfw-pair-gloss wfw-gap-btn"
        onClick={() => onAnnotateWord(bare)}
        aria-label={`Annotate ${bare}`}
        title={`Annotate “${bare}” to give it a word-for-word gloss`}
      >
        +
      </button>
    );
  };

  return (
    <div className="wfw-preview" role="group" aria-label={`Word for word: ${line.map((p) => p.gloss || '·').join(' ')}`}>
      {line.map((p, i) => (
        <span key={i} className="wfw-pair">
          <span className="wfw-pair-word">{p.word}</span>
          {p.gloss ? <span className="wfw-pair-gloss">{p.gloss}</span> : gap(p)}
        </span>
      ))}
    </div>
  );
}

export default WordForWordPreview;
